import { createTheme } from '@mui/material/styles';
import { getContrastTextColor, isLightColor } from '../utils/colorContrast';

export const DARK_DIALOG_BG = '#1A1C23';

const colors = {
  mint: {
    main: '#13FFA3',
    light: '#4DFFB8',
    dark: '#0ECC82',
    contrastText: '#000000',
  },
  rose: {
    main: '#FF3366',
    light: '#FF6688',
    dark: '#CC2952',
    contrastText: '#FFFFFF',
  },
  grey: {
    50: '#F8F9FA',
    100: '#F1F3F5',
    200: '#E9ECEF',
    300: '#DEE2E6',
    400: '#CED4DA',
    500: '#ADB5BD',
    600: '#868E96',
    700: '#495057',
    800: '#343A40',
    900: '#212529',
  },
};

export const commonStyles = {
  borderRadius: '16px',
  border: '1px solid',
  borderColor: 'rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease-in-out',
};

export const getCommonStyles = (isDarkMode) => ({
  borderRadius: '16px',
  border: '1px solid',
  borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease-in-out',
});

export const getTypographyStyles = (isDarkMode) => ({
  fontFamily: 'inherit',
  color: isDarkMode ? '#fff' : 'inherit',
});

/** Primary UI text — black in light mode, white in dark mode */
export const getAppTextPrimary = (isDarkMode) => (isDarkMode ? '#fff' : '#0d0d0d');

/** Page-level typography: one colour, hierarchy via font weight (overrides modern-dashboard.css) */
export const appTypographySx = (isDarkMode) => ({
  '& .MuiTypography-root': {
    color: `${getAppTextPrimary(isDarkMode)} !important`,
  },
  '& .MuiTypography-h4': { fontWeight: '700 !important' },
  '& .MuiTypography-h5': { fontWeight: '700 !important' },
  '& .MuiTypography-h6': { fontWeight: '600 !important' },
  '& .MuiTypography-subtitle1': { fontWeight: '600 !important' },
  '& .MuiTypography-subtitle2': { fontWeight: '500 !important' },
  '& .MuiTypography-body1': { fontWeight: '400 !important' },
  '& .MuiTypography-body2': { fontWeight: '500 !important' },
  '& .MuiTypography-caption': { fontWeight: '600 !important' },
  '& .MuiTypography-overline': { fontWeight: '700 !important' },
});

export const getDialogCommonStyles = (isDarkMode) => {
  const typographyStyles = getTypographyStyles(isDarkMode);
  return {
    borderRadius: '16px',
    '& .MuiTypography-root': typographyStyles,
    '& .MuiTableCell-root': typographyStyles,
    '& .MuiListItemText-primary': typographyStyles,
    '& .MuiListItemText-secondary': typographyStyles,
    '& .MuiChip-label': typographyStyles,
    '& .MuiButton-root': typographyStyles,
    '& .MuiInputBase-input': typographyStyles,
    '& .MuiInputLabel-root': typographyStyles,
    '& .MuiAlert-message': typographyStyles,
    '& .MuiDialogContentText-root': typographyStyles,
    '& .MuiDialogTitle-root': typographyStyles,
  };
};

export const getDialogPaperSx = (isDarkMode) => {
  const borderColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  return {
    borderRadius: '16px',
    bgcolor: isDarkMode ? DARK_DIALOG_BG : '#FFFFFF',
    backgroundImage: 'none',
    color: isDarkMode ? '#E4E6EB' : 'inherit',
    border: '1px solid',
    borderColor,
    boxShadow: isDarkMode
      ? '0 24px 48px rgba(0, 0, 0, 0.55)'
      : '0 16px 40px rgba(15, 23, 42, 0.12)',
    ...getDialogCommonStyles(isDarkMode),
  };
};

export const getDialogBackdropProps = (isDarkMode) => ({
  sx: {
    backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.78)' : 'rgba(0, 0, 0, 0.5)',
  },
});

export const getDialogSelectMenuProps = (isDarkMode) => ({
  sx: { zIndex: 1600 },
  PaperProps: {
    sx: {
      zIndex: 1600,
      mt: 0.5,
      borderRadius: '12px',
      bgcolor: isDarkMode ? DARK_DIALOG_BG : '#FFFFFF',
      backgroundImage: 'none',
      border: '1px solid',
      borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      boxShadow: isDarkMode ? '0 12px 32px rgba(0, 0, 0, 0.5)' : '0 8px 24px rgba(0, 0, 0, 0.12)',
      '& .MuiMenuItem-root': {
        color: isDarkMode ? '#E4E6EB' : 'inherit',
      },
    },
  },
});

export const getDialogAutocompleteProps = (isDarkMode) => {
  const surface = isDarkMode ? DARK_DIALOG_BG : '#FFFFFF';
  const borderColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  const paperSx = {
    zIndex: 1600,
    mt: 0.5,
    borderRadius: '12px',
    bgcolor: `${surface} !important`,
    backgroundColor: `${surface} !important`,
    backgroundImage: 'none',
    border: '1px solid',
    borderColor,
    boxShadow: isDarkMode ? '0 12px 32px rgba(0, 0, 0, 0.5)' : '0 8px 24px rgba(0, 0, 0, 0.12)',
    '& .MuiAutocomplete-option': {
      color: isDarkMode ? '#E4E6EB' : 'inherit',
    },
  };
  return {
    popper: {
      sx: { zIndex: 1600 },
      placement: 'bottom-start',
    },
    paper: { sx: paperSx },
    listbox: {
      sx: {
        bgcolor: surface,
        backgroundColor: surface,
        '& .MuiAutocomplete-option': {
          color: isDarkMode ? '#E4E6EB' : 'inherit',
        },
      },
    },
  };
};

/** sx for Autocomplete root — targets dropdown paper when componentsProps is not enough */
export const getAutocompleteDropdownSx = (isDarkMode) => ({
  '& .MuiAutocomplete-paper': {
    borderRadius: '12px',
    bgcolor: `${isDarkMode ? DARK_DIALOG_BG : '#FFFFFF'} !important`,
    backgroundImage: 'none',
    border: '1px solid',
    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    boxShadow: isDarkMode ? '0 12px 32px rgba(0, 0, 0, 0.5)' : '0 8px 24px rgba(0, 0, 0, 0.12)',
  },
  '& .MuiAutocomplete-listbox': {
    bgcolor: isDarkMode ? DARK_DIALOG_BG : '#FFFFFF',
    '& .MuiAutocomplete-option': {
      color: isDarkMode ? '#E4E6EB' : 'inherit',
    },
  },
});

/** Dialog source tabs / segmented controls */
export const getToggleTabSx = (isDarkMode, primaryColor, isActive, borderColor) => {
  const base = {
    px: 2,
    py: 0.75,
    borderRadius: '10px',
    fontWeight: 600,
    fontSize: '0.8rem',
    cursor: 'pointer',
    border: '1px solid',
    transition: 'all 0.15s ease',
  };
  if (!isActive) {
    return {
      ...base,
      borderColor,
      bgcolor: 'transparent',
      color: isDarkMode ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.6)',
    };
  }
  const solid = isDarkMode && !isLightColor(primaryColor);
  return {
    ...base,
    borderColor: primaryColor,
    bgcolor: solid ? primaryColor : `${primaryColor}22`,
    color: solid
      ? getContrastTextColor(primaryColor)
      : (isDarkMode && !isLightColor(primaryColor) ? '#E4E6EB' : primaryColor),
  };
};

/** Shared status/priority chip colours — bg uses 20% opacity suffix on hex */
export const STATUS_CHIP_STYLES = {
  active: { bg: '#10B98120', color: '#10B981' },
  pending: { bg: '#F59E0B20', color: '#F59E0B' },
  inactive: { bg: '#6B728020', color: '#6B7280' },
  urgent: { bg: '#EF444420', color: '#EF4444' },
  compliant: { bg: '#3B82F620', color: '#3B82F6' },
  overdue: { bg: '#FF336620', color: '#FF3366' },
  scheduled: { bg: '#3B82F620', color: '#3B82F6' },
  in_progress: { bg: '#8B5CF620', color: '#8B5CF6' },
  completed: { bg: '#10B98120', color: '#10B981' },
  cancelled: { bg: '#6B728020', color: '#6B7280' },
  low: { bg: '#10B98120', color: '#10B981' },
  medium: { bg: '#F59E0B20', color: '#F59E0B' },
  high: { bg: '#EF444420', color: '#EF4444' },
};

/** Standard list-page Paper wrapper (filters, tabs, empty states) */
export const getSectionPaperSx = (isDarkMode) => ({
  borderRadius: '16px',
  bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : '#FFFFFF',
  border: '1px solid',
  borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
  ...getCommonStyles(isDarkMode),
});

/** Standard page content stack — used inside PrivateRoute main */
export const getPageShellSx = (isDarkMode) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  color: getAppTextPrimary(isDarkMode),
  ...appTypographySx(isDarkMode),
});

/** MUI TextField / FormControl filter styling for list pages */
export const getControlSx = (isDarkMode, primaryColor) => {
  const filterFieldBg = isDarkMode ? 'rgba(255,255,255,0.04)' : '#FFFFFF';
  const filterBorderColor = isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      bgcolor: filterFieldBg,
      fontSize: '0.875rem',
      '& fieldset': { borderColor: filterBorderColor },
      '&:hover fieldset': {
        borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.18)',
      },
      '&.Mui-focused fieldset': { borderColor: primaryColor },
    },
  };
};

/** Clickable list row surface (Maintenance, Notifications, Design Style Lab §8) */
export const getListRowSx = (isDarkMode, primaryColor) => ({
  p: { xs: 2, sm: 2.5 },
  borderRadius: '12px',
  bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#FFFFFF',
  border: '1px solid',
  borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  '&:hover': {
    bgcolor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)',
    borderColor: `${primaryColor}55`,
  },
});

const getTheme = (mode, primaryColor) => createTheme({
  palette: {
    mode,
    primary: {
      main: primaryColor || colors.mint.main,
      light: primaryColor || colors.mint.light,
      dark: primaryColor || colors.mint.dark,
      contrastText: mode === 'dark' ? '#000000' : '#FFFFFF',
    },
    secondary: {
      main: colors.rose.main,
      light: colors.rose.light,
      dark: colors.rose.dark,
      contrastText: '#FFFFFF',
    },
    grey: colors.grey,
    background: {
      default: mode === 'dark' ? '#0F172A' : colors.grey[50],
      paper: mode === 'dark' ? '#1A1C23' : '#FFFFFF',
    },
    text: {
      primary: mode === 'dark' ? '#E4E6EB' : '#0d0d0d',
      secondary: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#374151',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          ...(theme.palette.mode === 'dark' && {
            backgroundImage: 'none',
            backgroundColor: DARK_DIALOG_BG,
          }),
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...(theme.palette.mode === 'dark' && {
            backgroundColor: 'rgba(0, 0, 0, 0.78)',
          }),
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...(theme.palette.mode === 'dark' && {
            backgroundColor: DARK_DIALOG_BG,
          }),
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...(theme.palette.mode === 'dark' && {
            backgroundColor: DARK_DIALOG_BG,
          }),
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...(theme.palette.mode === 'dark' && {
            backgroundColor: DARK_DIALOG_BG,
          }),
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          ...(theme.palette.mode === 'dark' && {
            backgroundImage: 'none !important',
            backgroundColor: `${DARK_DIALOG_BG} !important`,
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }),
        }),
        list: ({ theme }) => ({
          ...(theme.palette.mode === 'dark' && {
            backgroundColor: DARK_DIALOG_BG,
          }),
        }),
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: ({ theme }) => ({
          ...(theme.palette.mode === 'dark' && {
            backgroundImage: 'none',
            backgroundColor: DARK_DIALOG_BG,
          }),
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: ({ theme }) => ({
          ...(theme.palette.mode === 'dark' && {
            backgroundImage: 'none !important',
            backgroundColor: `${DARK_DIALOG_BG} !important`,
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }),
        }),
        listbox: ({ theme }) => ({
          ...(theme.palette.mode === 'dark' && {
            backgroundColor: DARK_DIALOG_BG,
          }),
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '8px 16px',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&.Mui-disabled': {
            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: mode === 'dark' ? '#E4E6EB' : 'inherit',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          padding: '1.5rem',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {},
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '0.5rem',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
        },
      },
    },
  },
});

export default getTheme;
export { getAppSurfaceBg, getAppShellTextColor, getTopbarBg, getTopbarTextColor, getTopbarMutedTextColor, getTopbarControlColor, DARK_SURFACE_BG, LIGHT_SURFACE_DEFAULT, DARK_TOPBAR_BG, LIGHT_TOPBAR_DEFAULT } from './surfaces';
